import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand, FAQ, SectionHeader, FeatureGrid, TableOfContents } from "@/components/site/Blocks";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";
import midTermRentalHero from "@/assets/hero/hero-mid-term-rental.jpg";

const FAQS = [
  {
    q: "What is a mid-term rental?",
    a: "A mid-term rental is a furnished residential property leased for roughly 30 days to 12 months, most often to travel nurses and allied health staff, relocating employees, insurance-displaced families, and traveling trades crews. It sits between a short-term vacation rental and a standard annual lease.",
  },
  {
    q: "Can you finance a mid-term rental with a DSCR loan?",
    a: "Yes. Mid-term rentals are business-purpose, non-owner-occupied investment property, which is exactly the profile DSCR programs are built for. The key underwriting question is which rent figure qualifies the property — most programs use the long-term market rent from the appraiser's rent schedule (Form 1007) as the qualifying income, with mid-term premiums treated as upside rather than qualifying income.",
  },
  {
    q: "Do lenders use mid-term rent or long-term market rent to qualify?",
    a: "It varies by program. The conservative and most common approach is to qualify on the appraiser's long-term market rent, because 30-to-90-day furnished leases are not backed by a 12-month lease. Some programs will consider documented mid-term income history. Program terms differ and are subject to full underwriting; nothing here is an offer of credit.",
  },
  {
    q: "How is a mid-term rental different from a short-term rental for financing?",
    a: "Short-term rental programs often qualify on documented nightly revenue from platform statements or a market data report, and lenders apply tighter LTV caps because revenue is seasonal. Mid-term rentals produce steadier monthly income with fewer turnovers, so they frequently qualify under standard DSCR terms using market rent instead of a specialty STR calculation.",
  },
  {
    q: "Are mid-term rentals exempt from short-term rental ordinances?",
    a: "Often, but never assume it. Many municipal short-term rental ordinances define a short-term stay as fewer than 30 days, so a 30-day minimum lease falls outside that definition. Other jurisdictions regulate any furnished rental under a year, and HOA documents may impose stricter minimums than the city. Verify the ordinance and HOA rules for the specific address before you buy.",
  },
  {
    q: "How much more does a mid-term rental earn than a long-term lease?",
    a: "It depends entirely on the submarket and the demand driver. Operators typically target a premium over the unfurnished market rent to compensate for furnishings, utilities, internet, cleaning between stays, and higher vacancy exposure. Because the premium is not guaranteed, underwrite the deal so it still works at long-term market rent — that is the same standard your lender will apply.",
  },
  {
    q: "Do mid-term rental loans require a specific occupancy history before closing?",
    a: "Most DSCR programs qualify on the appraiser's market rent rather than a trailing occupancy or booking history, so a newly acquired or newly furnished mid-term property can typically still qualify. Documented rent history can help but is generally not a hard requirement.",
  },
  {
    q: "Can I refinance an existing mid-term rental with a DSCR loan?",
    a: "Yes. Rate-and-term and cash-out refinance options are available on stabilized non-owner-occupied mid-term rentals, underwritten the same way as a purchase using appraised market rent and current leverage and credit tier. Cash-out proceeds are commonly used to furnish additional units or expand the portfolio.",
  },
  {
    q: "Does furnishing a property change the appraised value used for financing?",
    a: "Typically no — appraisals for investment property financing are based on comparable sales of the real estate itself, not the furniture package inside it. Furnishing cost is usually treated as a separate operating investment outside the loan-to-value calculation.",
  },
  {
    q: "Are corporate housing and mid-term rentals financed the same way?",
    a: "Generally yes, since both describe furnished stays longer than 30 days to a non-transient tenant. The underwriting approach is the same DSCR framework using long-term market rent, regardless of whether the tenant is an individual traveler or a corporate housing account.",
  },
  {
    q: "Do I need a property manager to qualify for a mid-term rental loan?",
    a: "No. Self-managed and professionally managed mid-term rentals are both eligible, since DSCR underwriting is based on the property's income potential rather than who manages the leasing. A dedicated mid-term operator or manager can still improve occupancy in practice.",
  },
  {
    q: "What down payment should I expect on a mid-term rental purchase?",
    a: "Down payment requirements follow the same leverage bands as other DSCR investment property purchases and depend on credit tier, DSCR, and property type. Illustrative figures are subject to credit review, appraisal, and full underwriting rather than a fixed percentage guaranteed here.",
  },
];

const TOC = [
  { href: "#what", label: "What a mid-term rental is" },
  { href: "#financing", label: "How financing works" },
  { href: "#pricing", label: "Live DSCR pricing" },
  { href: "#underwriting", label: "Underwriting the deal" },
  { href: "#fit", label: "Who it fits" },
  { href: "#faq", label: "FAQ" },
];

export const Route = createFileRoute("/mid-term-rental-loans")({
  head: () =>
    buildHead({
      title: "Mid-Term Rental Loans — DSCR Financing for 30+ Day Rentals",
      description:
        "DSCR financing for mid-term rentals: furnished 30-to-364-day properties for travel nurses, relocations and insurance housing. See how lenders qualify the rent and price it.",
      url: "/mid-term-rental-loans",
      keywords:
        "mid-term rental loans, mid term rental financing, DSCR loan mid-term rental, travel nurse rental financing, furnished rental loan, 30 day minimum rental loan, medium term rental investment loan, corporate housing financing, MTR investing loan, insurance housing rental loan, furnished rental DSCR",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Mid-Term Rental Loans", url: "/mid-term-rental-loans" },
      ],
      service: { name: "Mid-Term Rental Loan Financing" },
      faqs: FAQS,
    }),
  component: MidTermRentalPage,
});

function MidTermRentalPage() {
  return (
    <SiteShell>
      <PageHero
        eyebrow="Mid-term rental financing"
        title="Mid-Term Rental Loans: DSCR Financing for 30+ Day Furnished Rentals"
        subtitle="Furnished 30-to-364-day rentals for travel nurses, relocating professionals and insurance-displaced families are business-purpose investment property — and they finance on DSCR terms. Rates and terms are subject to change and full underwriting; this is not an offer of credit."
        primaryHref="/apply?product=dscr-loans"
        primaryLabel="Get Your Personalized Rate Quote"
        secondaryHref="/rates"
        secondaryLabel="See Today's Rates"
        image={{
          src: midTermRentalHero,
          alt: "Furnished mid-term rental living room with a dedicated workspace and luggage ready for an extended professional stay",
        }}
      />
      <PageEssentials hideToc />

      <Section>
        <div className="mx-auto max-w-4xl">
          <TableOfContents items={TOC} />
        </div>
      </Section>

      <Section id="what">
        <div className="mx-auto max-w-4xl space-y-4 text-sm text-foreground/90">
          <SectionHeader
            title="What a mid-term rental is"
            intro="Thirty days to twelve months, furnished, utilities included — the segment between a vacation rental and an annual lease."
          />
          <p>
            A mid-term rental (MTR) is a furnished residential unit leased for roughly 30 days to under a year. The demand is
            occupational rather than recreational: travel nurses and allied health contractors on 13-week assignments,
            relocating corporate employees waiting on a home purchase, families displaced by a claim while their house is
            repaired, and traveling trades crews on multi-month projects.
          </p>
          <p>
            The operating profile is genuinely different from a short-term rental. Turnovers drop from dozens a year to a
            handful, cleaning and platform fees fall with them, and revenue stops swinging with the tourist season. In exchange
            you carry the furnishings, utilities, and internet, and a vacant month between assignments costs a full month of
            rent rather than a slow weekend.
          </p>
          <p>
            For financing purposes the critical point is this: an MTR is non-owner-occupied property held for business purpose.
            It is not a second home, and it is not a consumer transaction — which is why it belongs on a{" "}
            <Link to="/dscr-loans" className="text-brand underline">DSCR loan</Link> rather than a conventional investment
            mortgage that qualifies you on personal tax returns.
          </p>
        </div>
      </Section>

      <Section id="financing" className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            title="How lenders finance a mid-term rental"
            intro="The property qualifies on income, not on your W-2s or tax returns."
          />
          <div className="mt-6">
            <FeatureGrid
              items={[
                {
                  title: "Qualifying rent",
                  body: "Most DSCR programs qualify on the appraiser's long-term market rent (Form 1007), treating the furnished premium as upside rather than qualifying income. Underwrite the deal so it clears at market rent.",
                },
                {
                  title: "Entity vesting",
                  body: "Title can be held in an LLC, LP, or trust where the program allows, with members providing a personal guarantee. That keeps the asset inside your investing entity.",
                },
                {
                  title: "Business-purpose only",
                  body: "The property must be non-owner-occupied and held for investment. You cannot occupy the unit between tenancies and keep the loan compliant.",
                },
                {
                  title: "Furnishings are not collateral",
                  body: "Appraised value is the real property. Budget furniture, housewares and setup as cash out of pocket — it typically does not get financed into the loan.",
                },
                {
                  title: "Reserves",
                  body: "Programs generally require post-close reserves in months of PITIA. Mid-term operators should carry more than the minimum because vacancy arrives one full month at a time.",
                },
                {
                  title: "Prepayment structure",
                  body: "DSCR notes often carry a step-down prepayment penalty. If you may sell or refinance early, price the buy-down against the rate before you lock.",
                },
              ]}
            />
          </div>
        </div>
      </Section>

      <LiveDscrRateSection
        strategyCards
        id="pricing"
        eyebrow="Live pricing · DSCR"
        heading="Live DSCR pricing for mid-term rental scenarios"
        paragraphs={[
          "These are pricing responses returned for a sample business-purpose DSCR scenario on non-owner-occupied investment property — rate, points or lender credit, estimated principal and interest, and lock period from the same quote. Pricing changes intraday, may not be available at commitment or closing, and is not a loan approval, commitment, or guarantee of financing.",
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your own DSCR scenario" },
          { to: "/dscr-calculator", label: "Run DSCR on your MTR" },
        ]}
      />

      <Section id="underwriting">
        <div className="mx-auto max-w-4xl space-y-4 text-sm text-foreground/90">
          <SectionHeader
            title="Underwriting a mid-term rental the way a lender will"
            intro="Four assumptions decide whether the deal survives contact with an appraisal."
          />
          <ol className="list-decimal space-y-3 pl-5">
            <li>
              <strong>Qualify at long-term market rent.</strong> Run{" "}
              <Link to="/dscr-calculator" className="text-brand underline">the DSCR calculator</Link> using the unfurnished
              market rent for the address. If DSCR only clears using the furnished premium, the loan is fragile.
            </li>
            <li>
              <strong>Model higher vacancy, not lower.</strong> Assignments end. A 10%–15% vacancy assumption is realistic for a
              first-year MTR, versus 5% on an annual lease.
            </li>
            <li>
              <strong>Own the operating costs a landlord normally does not.</strong> Electric, water, gas, internet, streaming,
              lawn, pest, and mid-stay cleans are all yours. Load them into{" "}
              <Link to="/cash-on-cash-return-calculator" className="text-brand underline">the cash-on-cash calculator</Link>{" "}
              before you call the deal profitable.
            </li>
            <li>
              <strong>Verify the ordinance and HOA in writing.</strong> Many cities define short-term as fewer than 30 days, so
              a 30-day minimum falls outside the rule — but plenty of HOAs impose 6- or 12-month minimums that override it.
            </li>
          </ol>
          <p>
            Two comparisons worth making before you commit to a strategy: an MTR against a{" "}
            <Link to="/airbnb-short-term-rental-loans" className="text-brand underline">short-term rental</Link>, where
            revenue is higher but so are turnover, regulation, and seasonality; and an MTR against a standard annual lease,
            where the gross rent is lower but the operating burden nearly disappears.
          </p>
        </div>
      </Section>

      <Section id="fit" className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-5xl">
          <SectionHeader title="Who mid-term rentals fit" intro="And who is better served by another strategy." />
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-semibold text-foreground">Good fit</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-foreground/90">
                <li>Properties within a short drive of a large hospital system, university medical center, or major employer campus</li>
                <li>Markets with restrictive short-term rental ordinances but strong professional demand</li>
                <li>Investors who want furnished-rental economics without nightly-stay operations</li>
                <li>Portfolios that need steadier month-to-month income than a vacation rental provides</li>
                <li>Entity-vested buyers who want DSCR qualification instead of tax-return underwriting</li>
              </ul>
            </div>
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-semibold text-foreground">Look elsewhere</h3>
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm text-foreground/90">
                <li>Deals that only clear DSCR using the furnished premium</li>
                <li>Submarkets with no institutional demand driver within a reasonable commute</li>
                <li>HOAs with a 6- or 12-month minimum lease term</li>
                <li>Investors who need fully passive income — MTR is an operating business</li>
                <li>Any scenario where the borrower intends to occupy the property, which no business-purpose program permits</li>
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section id="faq">
        <div className="mx-auto max-w-4xl">
          <SectionHeader title="Mid-term rental financing FAQ" intro="The questions investors ask before their first MTR." />
          <div className="mt-6">
            <FAQ items={FAQS} />
          </div>
        </div>
      </Section>

      <Section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-4xl">
          <h2 className="font-display text-2xl font-bold text-foreground">Keep going</h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { to: "/dscr-loans" as const, label: "DSCR loans — the core program" },
              { to: "/airbnb-short-term-rental-loans" as const, label: "Short-term rental financing" },
              { to: "/dscr-calculator" as const, label: "DSCR calculator" },
              { to: "/cash-on-cash-return-calculator" as const, label: "Cash-on-cash return calculator" },
              { to: "/best-rental-markets" as const, label: "Best rental markets" },
              { to: "/dscr-loan-requirements" as const, label: "DSCR loan requirements" },
            ].map((l) => (
              <li key={l.to}><Link to={l.to} className="text-brand underline">{l.label}</Link></li>
            ))}
          </ul>
        </div>
      </Section>

      <CTABand
        title="Financing your next mid-term rental"
        body="Share the address and the market rent and we'll return an illustrative DSCR structure — business-purpose, non-owner-occupied investment property only, subject to full underwriting."
      />
    <PageOutro />
      </SiteShell>
  );
}
